import { ServiceResponse } from '@nx-smartmonkey/shared/hooks';
import { useMutation } from '@tanstack/react-query';
import { generatePath, useLocation, useNavigate } from 'react-router-dom';

import { useEffect, useMemo, useState } from 'react';
import { onboardingService } from '../../../application/OnboardingService';
import * as routing from '../../../constants/Routing';
import { OrganizationActionType } from '../../../context/organization/actions';
import { useOrganizationContext } from '../../../context/organization/context';
import useErrorMiddleware from '../../../hooks/errorMiddleware/useErrorMiddleware';
import { useShowSnackbarMessage } from '../../../hooks/snackbarMessage/useShowSnackbarMessage';
import { CreateOrganizationOnboardingData } from './useInitializeOnboarding';

interface UseFinalizeOnboardingProps {
  projectId: string;
  onboardingData: CreateOrganizationOnboardingData;
}

type UseFinalizeOnboardingResponse = ServiceResponse<Function, undefined>;

export const useFinalizeOnboarding = ({
  projectId,
  onboardingData,
}: UseFinalizeOnboardingProps): UseFinalizeOnboardingResponse => {
  const navigate = useNavigate();
  const { sessionExpiredLogout } = useErrorMiddleware();
  const { showSnackbarMessage } = useShowSnackbarMessage();
  const { organizationState, organizationDispatch } = useOrganizationContext();
  const location = useLocation();
  const [triggerActivate, setTriggerActivate] = useState(false);

  const detailsPath = useMemo(
    () => generatePath(routing.PLANNER.PLAN_DETAILS.OVERVIEW, { projectId: projectId, planId: onboardingData.plan_id }),
    [onboardingData.plan_id, projectId]
  );

  useEffect(() => {
    if (location.pathname === detailsPath && triggerActivate) {
      organizationState.organization!.active = true;
      organizationDispatch({
        type: OrganizationActionType.UPDATE_ORGANIZATION_SUCCESS,
        payload: { organization: organizationState.organization! },
      });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location, triggerActivate]);

  const { isLoading, isSuccess, isError, mutate } = useMutation(
    (): Promise<void> => onboardingService.finalizeOnboarding(),
    {
      onSuccess: () => {
        navigate(detailsPath);
        setTriggerActivate(true);
      },
      onError: (err: any) => {
        console.info(`Finalize Onboarding Error:`, err);

        if (err?.response?.status === 401) {
          sessionExpiredLogout();
          return;
        }
        showSnackbarMessage({
          messageIntlId: `infoerror.${err?.response?.data?.messageId ?? `network_error`}`,
        });
      },
    }
  );

  return {
    error: isError,
    success: isSuccess,
    isLoading: isLoading,
    run: mutate,
  };
};
